import {
    getFirestore,
    doc,
    getDoc,
    collection,
    getDocs,
    query,
    where,
    orderBy,
    limit,
    writeBatch,
    setDoc
  } from "firebase/firestore";
  import { Result } from "../../models/result";
  import { OnlinePayment } from "../../models/online_payment";
  import { FeeReceipt } from "../../models/fee_receipt";
  import { FeeDue } from "../../models/fee_due";

  /**
   * Gets online payments from Firestore
   * @param {string} schoolId - School ID
   * @param {number} limitCount - Limit number of records (optional)
   * @returns {Promise<Result>} Result with online payments array
   */
  export const getOnlinePayments = async (schoolId, limitCount = null) => {
    try {
      if (!schoolId) {
        return new Result({ success: false, message: "School ID is required" });
      }

      const db = getFirestore();
      const paymentsRef = collection(db, "schools", schoolId, "online_payments");
      
      // Build query with ordering by time (newest first)
      let paymentsQuery = query(paymentsRef, orderBy("time", "desc"));
      
      // Add limit if specified
      if (limitCount && limitCount > 0) {
        paymentsQuery = query(paymentsQuery, limit(limitCount));
      }

      const querySnapshot = await getDocs(paymentsQuery);
      
      const payments = [];
      querySnapshot.forEach((doc) => {
        const payment = OnlinePayment.fromMap(doc.data(), doc.id);
        payments.push(payment);
      });

      return new Result({ 
        value: payments
      });

    } catch (error) {
      console.error("Error getting online payments:", error);
      return new Result({ 
        success: false, 
        message: `Failed to get online payments: ${error.message}` 
      });
    }
  };

  /**
   * Gets fee receipts from Firestore
   * @param {string} schoolId - School ID
   * @param {string} academicYear - Academic year
   * @param {number} limitCount - Limit number of records (optional)
   * @returns {Promise<Result>} Result with fee receipts array
   */
  export const getFeeReceipts = async (schoolId, academicYear, limitCount = null) => {
    try {
      if (!schoolId) {
        return new Result({ message: "School ID is required" });
      }

      if (!academicYear) {
        return new Result({  message: "Academic year is required" });
      }

      const db = getFirestore();
      const receiptsRef = collection(db, "schools", schoolId, "fee_data", academicYear, "fee_records");
      
      // Build query with ordering by date (newest first)
      let receiptsQuery = query(receiptsRef, orderBy("date", "desc"), limit(100));
      
      // Add limit if specified
      if (limitCount && limitCount > 0) {
        receiptsQuery = query(receiptsQuery, limit(limitCount));
      }

      const querySnapshot = await getDocs(receiptsQuery);
      
      const receipts = [];
      querySnapshot.forEach((doc) => {
        const receipt = FeeReceipt.fromMap(doc.data(), doc.id);
        receipts.push(receipt);
      });

      return new Result({ 
        value: receipts
      });

    } catch (error) {
      console.error("Error getting fee receipts:", error);
      return new Result({ 
        success: false, 
        message: `Failed to get fee receipts: ${error.message}` 
      });
    }
  };

  /**
   * Gets fee dues from Firestore
   * @param {string} schoolId - School ID
   * @param {number} limitCount - Limit number of records (optional)
   * @returns {Promise<Result>} Result with fee dues array
   */
  export const getFeeDue = async (schoolId, limitCount = null) => {
    try {
      if (!schoolId) {
        return new Result({ success: false, message: "School ID is required" });
      }

      const db = getFirestore();
      const duesRef = collection(db, "schools", schoolId, "fee_due");
      
      // Build query with ordering by date (oldest first for dues)
      let duesQuery = query(duesRef, orderBy("date", "asc"));
      
      // Add limit if specified
      if (limitCount && limitCount > 0) {
        duesQuery = query(duesQuery, limit(limitCount));
      }

      const querySnapshot = await getDocs(duesQuery);
      
      const dues = [];
      querySnapshot.forEach((doc) => {
        const due = FeeDue.fromMap(doc.data(), doc.id);
        dues.push(due);
      });

      return new Result({ 
        success: true, 
        value: dues,
        message: `Found ${dues.length} fee dues`
      });

    } catch (error) {
      console.error("Error getting fee dues:", error);
      return new Result({ 
        success: false, 
        message: `Failed to get fee dues: ${error.message}` 
      });
    }
  };

  /**
   * Gets online payments by admission number
   * @param {string} schoolId - School ID
   * @param {string} admissionNo - Admission number
   * @returns {Promise<Result>} Result with online payments array
   */
  export const getOnlinePaymentsByAdmission = async (schoolId, admissionNo) => {
    try {
      if (!schoolId || !admissionNo) {
        return new Result({ success: false, message: "School ID and admission number are required" });
      }

      const db = getFirestore();
      const paymentsRef = collection(db, "schools", schoolId, "online_payments");
      const paymentsQuery = query(
        paymentsRef, 
        where("admissionNo", "==", admissionNo),
        orderBy("time", "desc")
      );

      const querySnapshot = await getDocs(paymentsQuery);
      
      const payments = [];
      querySnapshot.forEach((doc) => {
        const payment = OnlinePayment.fromMap(doc.data(), doc.id);
        payments.push(payment);
      });

      return new Result({ 
        value: payments
      });

    } catch (error) {
      console.error("Error getting payments by admission:", error);
      return new Result({ 
        success: false, 
        message: `Failed to get payments: ${error.message}` 
      });
    }
  };

  /**
   * Gets fee dues by admission number
   * @param {string} schoolId - School ID
   * @param {string} admissionNo - Admission number
   * @returns {Promise<Result>} Result with fee dues array
   */
  export const getFeeDuesByAdmission = async (schoolId, admissionNo) => {
    try {
      if (!schoolId || !admissionNo) {
        return new Result({ success: false, message: "School ID and admission number are required" });
      }

      const db = getFirestore();
      const duesRef = collection(db, "schools", schoolId, "fee_due");
      const duesQuery = query(
        duesRef, 
        where("admissionNo", "==", admissionNo),
        orderBy("date", "asc")
      );

      const querySnapshot = await getDocs(duesQuery);
      
      const dues = [];
      querySnapshot.forEach((doc) => {
        const due = FeeDue.fromMap(doc.data(), doc.id);
        dues.push(due);
      });

      return new Result({ 
        value: dues
      });

    } catch (error) {
      console.error("Error getting dues by admission:", error);
      return new Result({ 
        success: false, 
        message: `Failed to get dues: ${error.message}` 
      });
    }
  };

/**
 * Uploads an array of fee receipts to Firestore
 * @param {string} schoolId - School ID
 * @param {string} academicYear - Academic year
 * @param {Array<FeeReceipt>} feeReceipts - Array of fee receipt objects
 * @returns {Promise<Result>} Result with upload status
 */
export const uploadFeeReceipts = async (schoolId, academicYear, feeReceipts) => {
  try {
    if (!schoolId) {
      return new Result({ success: false, message: "School ID is required" });
    }

    if (!academicYear) {
      return new Result({ success: false, message: "Academic year is required" });
    }

    if (!feeReceipts || !Array.isArray(feeReceipts) || feeReceipts.length === 0) {
      return new Result({ success: false, message: "Fee receipts array is required and must not be empty" });
    }

    const db = getFirestore();
    
    // Maximum batch size in Firestore is 500 operations
    const BATCH_SIZE = 500;
    let uploadedCount = 0;
    
    // Process receipts in batches
    for (let i = 0; i < feeReceipts.length; i += BATCH_SIZE) {
      const batchReceipts = feeReceipts.slice(i, i + BATCH_SIZE);
      const currentBatch = writeBatch(db);
      
      for (const receipt of batchReceipts) {
        // Validate receipt structure
        if (!receipt) {
          console.warn("Skipping null/undefined receipt");
          continue;
        }

        // Convert to FeeReceipt instance if it's not already
        let feeReceiptInstance;
        if (receipt instanceof FeeReceipt) {
          feeReceiptInstance = receipt;
        } else {
          feeReceiptInstance = new FeeReceipt(receipt);
        }

        // Generate document reference with custom ID format: admissionNo_voucherNo
        let docRef;
        let documentId = null;
        
        // Create document ID from admissionNo and voucherNo to prevent duplicates
        if (feeReceiptInstance.admissionNo && feeReceiptInstance.voucherNo) {
          documentId = `${feeReceiptInstance.admissionNo}_${feeReceiptInstance.voucherNo}`;
          docRef = doc(db, "schools", schoolId, "fee_data", academicYear, "fee_records", documentId);
        } else {
          return new Result({message: "Admission number and voucher number are required" });
        }

        // Add to batch
        currentBatch.set(docRef, feeReceiptInstance.toMap());
      }
      
      // Commit current batch
      if (batchReceipts.length > 0) {
        await currentBatch.commit();
        uploadedCount += batchReceipts.length;
      }
    }

    return new Result({
      success: true,
      value: `Successfully uploaded ${uploadedCount} fee receipts`
    });

  } catch (error) {
    console.error("Error uploading fee receipts:", error);
    return new Result({
      message: `Failed to upload fee receipts: ${error.message}`
    });
  }
};

export const uploadFeeDues = async (schoolId, feeDues) => {
 
try{
  if (!schoolId) {
    return new Result({ success: false, message: "School ID is required" });
  }

  if (!feeDues || !Array.isArray(feeDues) || feeDues.length === 0) {
    return new Result({ success: false, message: "Fee dues array is required and must not be empty" });
  }

  const db = getFirestore();
  const duesRef = collection(db, "schools", schoolId, "fee_due");

  const BATCH_SIZE = 500;
  let uploadedCount = 0;
  for (let i = 0; i < feeDues.length; i += BATCH_SIZE) {
    const batchDues = feeDues.slice(i, i + BATCH_SIZE);
    const currentBatch = writeBatch(db);
    for (const due of batchDues) {
      ///convert to FeeDue instance if it's not already
      let feeDueInstance;
      if (due instanceof FeeDue) {
        feeDueInstance = due;
      } else {
        feeDueInstance = new FeeDue(due);
      }
      currentBatch.set(doc(duesRef, feeDueInstance.admissionNo), feeDueInstance.toMap());
      }
      await currentBatch.commit();
      uploadedCount += batchDues.length;
    }
  
  return new Result({
    success: true,
    value: `Successfully uploaded ${uploadedCount} fee dues`
  });

}catch(ex){
  console.error("Error uploading fee dues:", ex);
  return new Result({
    message: `Failed to upload fee dues: ${ex.message}`
  });
}
}
  